// Geometry of the tutorial video. Everything is drawn on a fixed 1280×720
// stage that is scaled to the player, so the cursor and the spotlight can aim
// at exact rectangles in any language and at any size.

export const STAGE_W = 1280;
export const STAGE_H = 720;

export const clamp01 = (value) => Math.min(1, Math.max(0, value));
export const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);
/** 0 → 1 while `p` goes from `from` to `to`. */
export const span = (p, from, to) => clamp01((p - from) / (to - from));
/** The first part of `text`, as if typed between `from` and `to`. */
export const typed = (text, p, from, to) => text.slice(0, Math.round(text.length * span(p, from, to)));

export const center = (rect) => ({ x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 });
export const grow = (rect, by = 6) => ({ x: rect.x - by, y: rect.y - by, w: rect.w + by * 2, h: rect.h + by * 2 });

// ── Calendar screen ─────────────────────────────────────────────────────
export const CAL = {
  navTakvim: { x: 40, y: 12, w: 104, h: 34 },
  prev: { x: 40, y: 72, w: 40, h: 40 },
  title: { x: 84, y: 72, w: 180, h: 40 },
  next: { x: 268, y: 72, w: 40, h: 40 },
  today: { x: 316, y: 78, w: 96, h: 28 },
  segmented: { x: 760, y: 76, w: 128, h: 32 },
  excel: { x: 898, y: 76, w: 86, h: 32 },
  addJob: { x: 994, y: 76, w: 104, h: 32 },
  addVisit: { x: 1108, y: 76, w: 132, h: 32 },
  summary: { x: 40, y: 124, w: 620, h: 30 },
  windows: { x: 690, y: 124, w: 120, h: 30 },
  hideCompleted: { x: 818, y: 124, w: 188, h: 30 },
  projects: { x: 1014, y: 124, w: 104, h: 30 },
  help: { x: 1126, y: 124, w: 114, h: 30 },
  panel: { x: 912, y: 168, w: 328, h: 528 },
};

export const GRID = { x: 40, y: 168, w: 856, header: 30, rowH: 99.6, rows: 5 };
export const COL_W = GRID.w / 7;

export const cellRect = (row, col) => ({
  x: GRID.x + col * COL_W,
  y: GRID.y + GRID.header + row * GRID.rowH,
  w: COL_W,
  h: GRID.rowH,
});

export const dayNumberRect = (row, col) => {
  const cell = cellRect(row, col);
  return { x: cell.x + 6, y: cell.y + 5, w: 28, h: 26 };
};

/** A visit or job bar; with windows off a visit shrinks to its visit day. */
export const barRect = (bar, showWindows = true) => {
  const single = bar.kind !== 'job' && !showWindows;
  const start = single ? bar.mark : bar.start;
  const end = single ? bar.mark : bar.end;
  const cell = cellRect(bar.row, start);
  return { x: cell.x + 4, y: cell.y + 36 + bar.lane * 24, w: (end - start + 1) * COL_W - 8, h: 21 };
};

// ── "Yeni Vizit" dialog ─────────────────────────────────────────────────
export const ADD = {
  dialog: { x: 240, y: 52, w: 800, h: 628 },
  close: { x: 984, y: 64, w: 40, h: 40 },
  project: { x: 264, y: 170, w: 364, h: 40 },
  patient: { x: 644, y: 170, w: 320, h: 40 },
  addPatient: { x: 972, y: 170, w: 40, h: 40 },
  date: { x: 264, y: 272, w: 240, h: 40 },
  windowStart: { x: 520, y: 272, w: 240, h: 40 },
  windowEnd: { x: 776, y: 272, w: 236, h: 40 },
  quick: { x: 264, y: 324, w: 520, h: 30 },
  quick3: { x: 614, y: 324, w: 80, h: 30 },
  summary: { x: 264, y: 364, w: 748, h: 52 },
  type: { x: 264, y: 474, w: 364, h: 40 },
  number: { x: 644, y: 474, w: 368, h: 40 },
  repeat: { x: 264, y: 566, w: 364, h: 40 },
  google: { x: 644, y: 566, w: 368, h: 40 },
  cancel: { x: 836, y: 630, w: 90, h: 38 },
  save: { x: 934, y: 630, w: 92, h: 38 },
};

// ── "Yeni İş" dialog ────────────────────────────────────────────────────
export const JOB = {
  dialog: { x: 340, y: 92, w: 600, h: 536 },
  close: { x: 884, y: 104, w: 40, h: 40 },
  title: { x: 364, y: 206, w: 552, h: 40 },
  project: { x: 364, y: 282, w: 552, h: 40 },
  start: { x: 364, y: 392, w: 268, h: 40 },
  end: { x: 648, y: 392, w: 268, h: 40 },
  time: { x: 364, y: 474, w: 268, h: 40 },
  cancel: { x: 714, y: 572, w: 90, h: 38 },
  save: { x: 812, y: 572, w: 104, h: 38 },
};

/** Rows of an open select list under `rect`. */
export const optionRect = (rect, index) => ({ x: rect.x, y: rect.y + rect.h + 8 + index * 34, w: rect.w, h: 34 });

// ── "Vizit Detayları" dialog ────────────────────────────────────────────
export const FORM = {
  dialog: { x: 40, y: 22, w: 1200, h: 676 },
  rail: { x: 40, y: 86, w: 256, h: 540 },
  progress: { x: 56, y: 100, w: 224, h: 104 },
  content: { x: 320, y: 100, w: 896 },
  missing: { x: 320, y: 198, w: 896, h: 38 },
  footer: { x: 40, y: 626, w: 1200, h: 72 },
  save: { x: 940, y: 642, w: 102, h: 40 },
  complete: { x: 1050, y: 642, w: 174, h: 40 },
  next: { x: 848, y: 642, w: 84, h: 40 },
};

export const stepRect = (index) => ({ x: 52, y: 218 + index * 52, w: 232, h: 46 });

// Step 2 (Yapılacaklar): checkbox rows, two columns
export const todoRowRect = (index) => ({ x: 336 + (index % 2) * 444, y: 344 + Math.floor(index / 2) * 52, w: 432, h: 44 });

// Step 3 (Ziyaret Formu)
export const FORM_FIELDS = {
  card1: { x: 320, y: 248, w: 896, h: 150 },
  card2: { x: 320, y: 408, w: 896, h: 100 },
  card3: { x: 320, y: 518, w: 896, h: 100 },
  types: [
    { x: 336, y: 306, w: 132, h: 34 },
    { x: 476, y: 306, w: 118, h: 34 },
    { x: 602, y: 306, w: 124, h: 34 },
    { x: 734, y: 306, w: 150, h: 34 },
    { x: 892, y: 306, w: 142, h: 34 },
  ],
  sourceYes: { x: 336, y: 364, w: 80, h: 30 },
  sourceNo: { x: 422, y: 364, w: 80, h: 30 },
  travelLocal: { x: 560, y: 364, w: 120, h: 30 },
  travelOutside: { x: 686, y: 364, w: 120, h: 30 },
  startTime: { x: 336, y: 456, w: 200, h: 38 },
  endTime: { x: 552, y: 456, w: 200, h: 38 },
  patientId: { x: 336, y: 566, w: 190, h: 38 },
  doctor: { x: 538, y: 566, w: 210, h: 38 },
  hospital: { x: 760, y: 566, w: 260, h: 38 },
  city: { x: 1032, y: 566, w: 168, h: 38 },
};

/** Fixed-width "Eksik alanlar" chips (width from the label length), left to right. */
export const missingChipRects = (labels) => {
  let x = 450;
  return labels.map((text) => {
    const w = Math.round(26 + text.length * 6.6);
    const rect = { x, y: 203, w, h: 28 };
    x += w + 6;
    return rect;
  });
};

// Step 7 (Visit Değerlendirme): answer pills and inputs
const yesNo = (x, y) => ({ yes: { x, y, w: 66, h: 30 }, no: { x: x + 72, y, w: 70, h: 30 } });
export const EVAL_FIELDS = {
  cards: [290, 372, 454, 536].map((y) => ({ x: 320, y, w: 896, h: 76 })),
  screenFail: yesNo(700, 322),
  dropPatient: yesNo(940, 322),
  protocolDeviation: yesNo(700, 404),
  processEndDate: { x: 700, y: 484, w: 190, h: 36 },
  slaTarget: { x: 940, y: 484, w: 150, h: 36 },
  issueOccurred: yesNo(700, 568),
};

// Steps 4–6: the add button of each optional step
export const EXTRA_BUTTON = { x: 1064, y: 300, w: 152, h: 38 };
export const FOOTER = {
  close: { x: 64, y: 642, w: 70, h: 40 },
  remove: { x: 142, y: 646, w: 128, h: 32 },
  back: { x: 756, y: 642, w: 84, h: 40 },
};
